import './RefundModal.css';
import React from 'react';
import { refundItems, RefundItemsStrategy } from '../../../../model/item';
import { RefundModel, RefundOption, ItemGroup } from '../../../../model/order';
import { ModalController } from '../../../../components/Modal/ControlledModal';
import { Form, useChanges } from '../../../../components/Form/Form';
import { RadioGroup } from '../../../../components/Form/Radio/RadioGroup';
import { TextField } from '../../../../components/Form/TextField/TextField';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { Label } from '../../../../components/Form/Label/Label';
import { Checkbox } from '../../../../components/Form/Checkbox/Checkbox';
import { Stack } from '../../../../components/Stack/Stack';

interface Props {
  orderId: string;
  itemGroups: ItemGroup[];
  itemIds: string[];
  controller: ModalController<RefundModel>;
  onChange: () => void;
}

export function RefundModal({ orderId, itemGroups, itemIds, controller, onChange }: Props): JSX.Element {
  const products = itemGroups.filter(
    (item) => item.product?.__typename === 'MerchProduct' || item.product?.__typename === 'ShippingProduct'
  );
  const events = itemGroups.filter((item) => item.product?.__typename === 'Event');

  const refundOptionIsNeeded = !!events.length;
  const bookDeliveryIsPossible = itemGroups.some((i) => i.product?.__typename === 'MerchProduct');

  const [, , model, trackChanges] = useChanges<RefundModel>({
    option: refundOptionIsNeeded ? 'refund-release' : undefined,
    markDeliveryAsSent: bookDeliveryIsPossible,
  });

  async function handleOnSubmit(): Promise<void> {
    try {
      const uiStrategyToAPIStrategy = {
        'refund-destroy': 'refund_only' as RefundItemsStrategy,
        'refund-block': 'refund_and_block' as RefundItemsStrategy,
        're-issue-tickets': 'refund_and_reissue' as RefundItemsStrategy,
        'refund-release': undefined,
      };
      await refundItems({
        orderId,
        itemIds,
        blockReason: model.blockReason,
        strategy: model.option && uiStrategyToAPIStrategy[model.option],
        markDeliveryAsSent: model.markDeliveryAsSent,
      });
      controller.close();
      onChange();
    } catch (e) {
      handleError(e);
    }
  }

  function availableOptions(): Array<{ label: string; value: RefundOption }> {
    return [
      {
        label: t.model.refund.refundAndRelease,
        value: 'refund-release',
      },
      {
        label: t.model.refund.refundAndBlock,
        value: 'refund-block' as RefundOption,
      },
      {
        label: t.model.refund.reIssueTickets,
        value: 're-issue-tickets',
      },
    ];
  }

  function isFormValid(): boolean {
    if (model.option === 'refund-block' && !model.blockReason) {
      return false;
    }

    return true;
  }

  return (
    <div className="refund-modal">
      <Form model={model} onInput={trackChanges}>
        {() => {
          controller.configure({
            onSubmit: handleOnSubmit,
            canSubmit: isFormValid(),
          });
          return (
            <>
              {refundOptionIsNeeded && (
                <Stack>
                  {!!products.length && (
                    <p className="refund-modal__products">{t.model.refund.refundModal.mixedWarning(events.length)}</p>
                  )}
                  <Label title={t.model.refund.refundModal.refundOptionsTickets(events.length)} />
                  <RadioGroup name="option" values={availableOptions()}></RadioGroup>
                  {model.option === 'refund-block' && (
                    <TextField required name="blockReason" label={t.model.contingent.blockReason} />
                  )}
                </Stack>
              )}
              {bookDeliveryIsPossible && (
                <>
                  <Label title={t.model.refund.refundModal.refundOptionsMerch} />
                  <Checkbox
                    name="markDeliveryAsSent"
                    className="refund-modal__checkbox-option"
                    label={t.model.refund.refundModal.markDeliveryAsSent}
                  />
                </>
              )}
            </>
          );
        }}
      </Form>
    </div>
  );
}
